import { pushToDatalayer } from '@luks/meta/src';
import {
  buildForm,
  buildLink,
  KitchenSinkRoute,
  OverviewPage,
  RouteSlotInput,
} from '@luks/ui';
import { memoize } from 'lodash';

import {
  BabySearchQuery,
  LiveBabyFragment,
  useInfiniteBabySearchQuery,
} from '../../../generated/queries';
import { cleanList } from '../../commons/cleanList';
import { formNavigate, useQueryParameters } from '../../commons/forms';
import { injectAdverts, isAdvert } from '../AdvertCollection.helpers';
import { buildDrupalImage } from '../Image';
import { ParagraphOptions } from './AllParagraphs';

type SearchInput = {
  name?: string;
  gender?: string;
  date?: string;
  location?: string;
};

const Form = buildForm<SearchInput>({
  initialValues: {
    name: '',
    gender: 'All',
    date: '',
    location: '',
  },
  onSubmit: formNavigate(),
  useInitialValues: useQueryParameters,
});

type BabyGalleryItem = Exclude<
  RouteSlotInput<typeof KitchenSinkRoute, 'body', 'BabyGallery'>['input'],
  Function
>['babies'][number];

const mapBaby = memoize(
  (item: LiveBabyFragment | AdvertFragment): BabyGalleryItem => {
    return isAdvert(item)
      ? {
          Image: buildDrupalImage(item.image, 'content'),
          title: item.title,
          description: item.text,
          cta: {
            Link: buildLink({ href: item.link.url }),
            text: item.link.text,
          },
          TextOrientation: item.textAlignment,
        }
      : {
          Image: buildDrupalImage(item.image, 'teaser'),
          Link: buildLink({ href: item.path }),
          name: item.title,
        };
  },
  (item) => `baby-${item.id}`,
);

const aggregateResults = (
  pages?: Array<BabySearchQuery>,
  advertCollection?: AdvertCollectionFragment,
) =>
  injectAdverts<LiveBabyFragment, AdvertFragment | undefined>(
    pages?.reduce(
      (acc, value) => [...acc, ...value.searchBabies.items],
      [] as Array<LiveBabyFragment>,
    ) || [],
    advertCollection,
  )
    .filter(cleanList)
    .map(mapBaby);

export function mapBabysearch(
  paragraph: ParagraphBabyGalleryFragment,
  context: { payload: ParagraphOptions; items: Array<any>; index: number },
): RouteSlotInput<typeof OverviewPage, 'body', 'BabyGallery'> {
  return {
    key: 'BabyGallery',
    input: function useBabyGallery() {
      const pageSize = 12;
      const { name, gender, ...params } =
        useQueryParameters<SearchInput>() || {};
      const filters = {
        ...params,
        title: name,
        gender: gender === 'All' ? '' : gender,
      };

      const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
      } = useInfiniteBabySearchQuery(
        {
          filters,
          limit: pageSize,
          offset: 0,
        },
        {
          keepPreviousData: true,
          getNextPageParam: (lastPage, allPages) => {
            const displayedItems = allPages.reduce(
              (acc, val) => acc + (val.searchBabies.items.length || 0),
              0,
            );

            return lastPage.searchBabies.total > displayedItems
              ? {
                  limit: pageSize,
                  filters,
                  offset: displayedItems,
                }
              : undefined;
          },
        },
      );

      if (
        data &&
        data.pages.length &&
        !isLoading &&
        !isFetching &&
        !isFetchingNextPage
      ) {
        const filterSearchTerm = filters.title;
        const filterType = paragraph.filters.gender.find(
          (it) => it.id === filters.gender,
        )?.title;
        const filterDate = filters.date;
        const filterLocation = paragraph.filters.location.find(
          (it) => it.id === filters.location,
        )?.title;
        const hasFilters =
          filterSearchTerm || filterType || filterDate || filterLocation;
        pushToDatalayer(
          {
            event: 'BabygalerieSearch',
            gtmFilters: {
              gtmSearchTerm: filterSearchTerm,
              gtmType: filterType,
              gtmDate: filterDate,
              gtmLocation: filterLocation,
            },
            currentResultsShown: data.pages
              .map((page) => page.searchBabies.items.length)
              .reduce((acc, val) => acc + val, 0),
            totalResultSet: data.pages.at(-1)?.searchBabies.total,
            currentPageNumber: data.pages.length,
            firstPageLoad: !hasFilters && data.pages.length === 1,
          },
          'baby-search',
        );
      }

      const latest = data?.pages.at(-1);
      return [
        {
          babies: aggregateResults(
            data?.pages,
            context.payload.advertCollection,
          ),
          genders: paragraph.filters.gender.map((item) => ({
            value: item.id,
            title: item.title,
          })),
          locations: paragraph.filters.location.map((item) => ({
            value: item.id,
            title: item.title,
          })),
          Form,
          infiniteScroll: context.items.length - 1 === context.index,
          MoreLink: hasNextPage
            ? buildLink({
                onClick: (ev) => {
                  ev.preventDefault();
                  fetchNextPage();
                },
              })
            : undefined,
        },
        isLoading || isFetching || isFetchingNextPage
          ? 102
          : data
          ? latest?.searchBabies?.total || 0 > 0
            ? 200
            : 404
          : 0,
      ];
    },
  };
}
